import React, { useEffect, useState } from "react";

/**
 * Composio OAuth popup landing page.
 *
 * Composio redirects the connect popup here once the hosted auth flow finishes,
 * appending `status=success` or `status=failed`. This page's only job is to tell
 * the window that opened it what happened and then close itself.
 *
 * Why this exists: the callback used to point at `/integrations`, which loaded
 * the entire Integrations page — full app shell and all — inside the 500x700
 * popup and then left it open forever. Users mid-onboarding got a stray window
 * covering the flow they were supposed to continue.
 *
 * Deliberately public (no PrivateRoute) and outside <Layout>: the popup should
 * never render app chrome, and it must never bounce to /login if the session
 * check is momentarily slow — it just needs to close.
 */
const ComposioCallback: React.FC = () => {
  const [closeBlocked, setCloseBlocked] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider") || "";
    // Composio appends status=success|failed; treat anything non-failed as
    // success so an unexpected value doesn't strand the popup.
    const failed = params.get("status") === "failed";

    try {
      // Mirrors the custom-OAuth flow's contract so existing OAUTH_SUCCESS
      // listeners pick this up unchanged. `source` lets Composio-specific
      // handlers tell it apart from the Nango/custom popups.
      window.opener?.postMessage(
        {
          type: failed ? "OAUTH_ERROR" : "OAUTH_SUCCESS",
          provider,
          source: "composio",
          timestamp: Date.now(),
        },
        window.location.origin,
      );
    } catch {
      // Opener gone or cross-origin — the parent's polling still resolves it.
    }

    window.close();

    // window.close() is a no-op for windows the script didn't open (e.g. if the
    // user completed auth in a normal tab). Show a message rather than a blank
    // page in that case.
    const timer = setTimeout(() => setCloseBlocked(true), 400);
    return () => clearTimeout(timer);
  }, []);

  if (!closeBlocked) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "24px",
        textAlign: "center",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#374151",
      }}
    >
      <div>
        <p style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 8px" }}>
          Connection complete
        </p>
        <p style={{ fontSize: "14px", margin: 0, color: "#6B7280" }}>
          You can close this window and return to SendUpdates.
        </p>
      </div>
    </div>
  );
};

export default ComposioCallback;
